import { useCallback, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

import type { MenuItem } from '@atlassian/universal-create/types';

import { splitButtonSpotlightMessages } from './messages.ts';
import { useCwRSpotlightSeenStorage } from './useCwRSpotlightSeenStorage.tsx';
import {
	useRenderCwRSpotlight,
	type SpotlightVariant,
	type UseRenderCwRSpotlightArgs,
} from './useRenderCwRSpotlight.tsx';

const MENU_ANCHOR_SELECTOR = '[data-testid="global-create-content-popover-menu--hidden"]';
const BUTTON_ANCHOR_SELECTOR = '[data-testid="uc-trigger-button-primary"]';

const SHARED_CHOREOGRAPHER_MESSAGE_ID = 'UniversalCreateCwRExperimentSpotlight';

type VariantConfig = Omit<
	UseRenderCwRSpotlightArgs,
	'isEnabled' | 'onCtaClick' | 'onClosed' | 'choreographerMessageId' | 'spotlightTarget'
>;

const MENU_VARIANT_CONFIG: VariantConfig = {
	anchorSelector: MENU_ANCHOR_SELECTOR,
	messages: {
		title: splitButtonSpotlightMessages.commonTitle,
		bodyLine1: splitButtonSpotlightMessages.ucDropdownBodyFirst,
		bodyLine2: splitButtonSpotlightMessages.ucDropdownBodySecond,
		cta: splitButtonSpotlightMessages.commonConfirm,
	},
	placement: 'left-end',
	requireAnchorForStart: false,
};

const BUTTON_VARIANT_CONFIG: VariantConfig = {
	anchorSelector: BUTTON_ANCHOR_SELECTOR,
	messages: {
		title: splitButtonSpotlightMessages.commonTitle,
		bodyLine1: splitButtonSpotlightMessages.cwrModalBodyFirst,
		bodyLine2: splitButtonSpotlightMessages.cwrModalBodySecond,
		cta: splitButtonSpotlightMessages.commonCreateWithRovo,
	},
	placement: 'bottom-start',
	requireAnchorForStart: true,
};

const SPOTLIGHT_VARIANT_CONFIGS: Record<SpotlightVariant, VariantConfig> = {
	button: BUTTON_VARIANT_CONFIG,
	menu: MENU_VARIANT_CONFIG,
};

export type UseCwRSpotlightOrchestratorArgs = {
	isMenuOpen: boolean;
	onCloseMenu?: () => void;
	onClick?: () => void;
	primaryActionItem?: MenuItem;
	enabled?: boolean;
};

export type UseCwRSpotlightOrchestratorResult = {
	onClick?: () => void;
	onCloseMenu: () => void;
	primaryActionItem?: MenuItem;
	spotlight: ReactNode;
};

export function useCwRSpotlightOrchestrator({
	isMenuOpen,
	onCloseMenu,
	onClick: dropdownOnClick,
	primaryActionItem,
	enabled = true,
}: UseCwRSpotlightOrchestratorArgs): UseCwRSpotlightOrchestratorResult {
	const { hasSeenSpotlight } = useCwRSpotlightSeenStorage();
	const [spotlightVariant, setSpotlightVariant] = useState<SpotlightVariant>('button');
	const [hasInteracted, setHasInteracted] = useState(false);

	const pendingVariantRef = useRef<SpotlightVariant | null>(null);
	const pendingOnClickRef = useRef<(() => void) | undefined>(undefined);
	const startSpotlightRef = useRef<() => boolean>(() => false);
	const stopSpotlightRef = useRef<() => void>(() => {});

	const flushPendingOnClick = useCallback(() => {
		const pending = pendingOnClickRef.current;
		pendingOnClickRef.current = undefined;
		pending?.();
	}, []);

	const spotlightVariantRef = useRef<SpotlightVariant>(spotlightVariant);
	spotlightVariantRef.current = spotlightVariant;

	const handleMenuClosed = useCallback(() => {
		const variantActiveOrPending =
			spotlightVariantRef.current === 'menu' || pendingVariantRef.current === 'menu';
		if (variantActiveOrPending) {
			stopSpotlightRef.current();
			pendingVariantRef.current = null;
		}
		onCloseMenu?.();
	}, [onCloseMenu]);

	const handleCtaClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
		if (spotlightVariantRef.current === 'menu') {
			// Prevent the click from bubbling to the underlying popup/menu
			// (e.g. as a click-outside) so the dropdown stays open when the
			// spotlight's primary action is pressed. Mirrors the reference
			// example at
			// platform/packages/navigation/universal-create/examples/29-menu-dropdown-spotlight-selector-only.tsx.
			event.preventDefault();
			event.stopPropagation();
			// Stop the spotlight but keep the menu dropdown open.
			stopSpotlightRef.current();
			pendingVariantRef.current = null;
		}
	}, []);

	const handleSpotlightClosed = useCallback(() => {
		if (spotlightVariantRef.current === 'button') {
			flushPendingOnClick();
		}
	}, [flushPendingOnClick]);

	const handleSpotlightBlocked = useCallback(
		(blockedVariant: SpotlightVariant) => {
			// Ignore stale blocked callbacks from older start attempts.
			if (pendingVariantRef.current !== blockedVariant) {
				return;
			}

			pendingVariantRef.current = null;

			if (blockedVariant === 'button') {
				flushPendingOnClick();
			}
		},
		[flushPendingOnClick],
	);

	const activeSpotlightConfig = SPOTLIGHT_VARIANT_CONFIGS[spotlightVariant];

	const { spotlight, startSpotlight, stopSpotlight } = useRenderCwRSpotlight({
		...activeSpotlightConfig,
		choreographerMessageId: SHARED_CHOREOGRAPHER_MESSAGE_ID,
		isEnabled: enabled && (spotlightVariant === 'button' || isMenuOpen),
		spotlightTarget: spotlightVariant,
		onCtaClick: handleCtaClick,
		onClosed: handleSpotlightClosed,
		onBlocked: handleSpotlightBlocked,
	});
	startSpotlightRef.current = startSpotlight;
	stopSpotlightRef.current = stopSpotlight;

	const scheduleSpotlightStart = useCallback(
		(variant: SpotlightVariant) => {
			pendingVariantRef.current = variant;

			if (spotlightVariantRef.current !== variant || !hasInteracted) {
				flushSync(() => {
					setHasInteracted(true);
					setSpotlightVariant(variant);
				});
			}

			const didStart = startSpotlightRef.current();
			if (!didStart) {
				pendingVariantRef.current = null;
				if (variant === 'button') {
					flushPendingOnClick();
				}
			}
		},
		[flushPendingOnClick, hasInteracted],
	);

	const interceptedDropdownOnClick = useCallback(() => {
		dropdownOnClick?.();

		if (hasSeenSpotlight || pendingVariantRef.current !== null) {
			return;
		}

		scheduleSpotlightStart('menu');
	}, [hasSeenSpotlight, dropdownOnClick, scheduleSpotlightStart]);

	const interceptedPrimaryActionItem = useMemo(() => {
		if (!primaryActionItem || hasSeenSpotlight) {
			return primaryActionItem;
		}

		const originalPrimaryOnClick = primaryActionItem.onClick;
		const interceptedPrimaryOnClick: MenuItem['onClick'] = (event) => {
			pendingOnClickRef.current = originalPrimaryOnClick
				? () => originalPrimaryOnClick(event)
				: undefined;
			scheduleSpotlightStart('button');
		};

		return { ...primaryActionItem, onClick: interceptedPrimaryOnClick };
	}, [hasSeenSpotlight, primaryActionItem, scheduleSpotlightStart]);

	if (!enabled) {
		return {
			onClick: dropdownOnClick,
			onCloseMenu: onCloseMenu ?? (() => {}),
			primaryActionItem,
			spotlight: null,
		};
	}

	return {
		onClick: hasSeenSpotlight ? dropdownOnClick : interceptedDropdownOnClick,
		onCloseMenu: handleMenuClosed,
		primaryActionItem: interceptedPrimaryActionItem,
		spotlight: hasInteracted ? spotlight : null,
	};
}
